import router from 'next/router';
import { Space } from '@goatapp/web-content-components';
import { useEffect, useState } from 'react';

import { bugsnag } from 'shared/utils/services/bugsnag';
import {
  DEFAULT_ASSET_WIDTH_BEFORE_UI_RENDERS,
  SPACE_VIEW_TYPES,
} from 'homepage/enums/contentConstants';
import { getBrandFeedTrackingInfo } from 'homepage/getBrandFeedSpaceTrackingInfo';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';

import { IModuleTrackingData } from 'homepage/types/ModuleTrackingData';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useWindowSize } from 'shared/hooks/useWindowSize';

import { ISpaceArticleSection } from 'shared/types/SpaceArticleSection';
import { FeatureFlags } from 'featureFlags/constants';

interface IBrandedSpaceContent {
  drawerName: string;
  draftId?: number;
  draftUuid?: string;
  isSingleSpaceBrandPage?: boolean;
  maxHeight?: number;
  maxWidth?: number;
  moduleTrackingData: IModuleTrackingData;
  section: ISpaceArticleSection;
  sectionIndex?: number;
  virtualMaxHeight?: number;
  virtualMaxWidth?: number;
}

export const BrandSpaceContent = ({
  drawerName,
  isSingleSpaceBrandPage = false,
  maxHeight = null,
  maxWidth = null,
  moduleTrackingData,
  section = null,
  draftId,
  draftUuid,
  virtualMaxHeight = null,
  virtualMaxWidth = null,
}: IBrandedSpaceContent) => {
  const { articles = [] } = section || {};

  const { isMobile, windowSize } = useWindowSize();

  const [canvasWidth, setCanvasWidth] = useState(DEFAULT_ASSET_WIDTH_BEFORE_UI_RENDERS);

  const newPerformanceSpaceFlag = useFeatureFlag(FeatureFlags.TEMP_WEB_NEW_PERFORMANCE_SPACE);
  const isNewSearchPageEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);

  const getCurrentSpace = () => {
    if (isMobile) {
      return articles?.find((article) => article?.viewType === SPACE_VIEW_TYPES.MOBILE_VIEW)
        ?.sections?.[0];
    }

    return articles?.find((article) => article?.viewType === SPACE_VIEW_TYPES.DESKTOP_VIEW)
      ?.sections?.[0];
  };

  const trackContentClick = (url) => {
    sendTrackingEvent('HOMEPAGE_LANDING_TAP', {
      drawer_name: drawerName,
      target_destination: url,
      service_name: isNewSearchPageEnabled ? 'consumer_search' : 'constructor',
    });

    try {
      if (draftUuid || draftId) {
        const data = getBrandFeedTrackingInfo(moduleTrackingData, url);

        sendTrackingEvent('CMS_MODULE_ITEM_TAP', {
          ...data,
          service_name: isNewSearchPageEnabled ? 'consumer_search' : 'constructor',
        });
      }
    } catch (error) {
      bugsnag(error, { context: 'Error tracking space click' });
    }
  };

  const redirectToArticleAssetUrl = (url: string) => {
    if (!url) {
      return;
    }
    trackContentClick(url);
    if (url?.includes('https://')) {
      window.location.href = url;
      return;
    }
    void router.push(url);
  };

  useEffect(() => {
    setCanvasWidth(windowSize?.width);
  }, [isMobile, windowSize]);

  if (articles?.length < 1) {
    return null;
  }

  const currentSpace = getCurrentSpace();

  if (!currentSpace) {
    return null;
  }

  if (isSingleSpaceBrandPage) {
    return (
      <Space
        space={currentSpace}
        maxWidth={canvasWidth}
        onItemLinkClick={redirectToArticleAssetUrl}
        usePerformanceSpace={newPerformanceSpaceFlag}
      />
    );
  }

  return !isMobile ? (
    <Space
      space={currentSpace}
      maxWidth={maxWidth}
      onItemLinkClick={redirectToArticleAssetUrl}
      virtualMaxWidth={virtualMaxWidth}
      usePerformanceSpace={newPerformanceSpaceFlag}
    />
  ) : (
    <Space
      space={currentSpace}
      maxHeight={maxHeight}
      maxWidth={maxWidth}
      virtualMaxHeight={virtualMaxHeight}
      virtualMaxWidth={virtualMaxWidth}
      onItemLinkClick={redirectToArticleAssetUrl}
      usePerformanceSpace={newPerformanceSpaceFlag}
    />
  );
};
